// Shared booking date helpers — mirrored from web app to keep keys identical.

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
const MONTH_KEYS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
] as const;

type DayKey = (typeof DAY_KEYS)[number];
type MonthKey = (typeof MONTH_KEYS)[number];

export interface DateEntry {
  dayKey: DayKey;
  monthKey: MonthKey;
  date: number;
  fullDate: string; // YYYY-MM-DD
}

export function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function startOfWeekMonday(d: Date): Date {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  const day = date.getDay() || 7;
  if (day !== 1) date.setDate(date.getDate() + (1 - day));
  return date;
}

export function addDays(d: Date, days: number): Date {
  const date = new Date(d);
  date.setDate(date.getDate() + days);
  return date;
}

export function generateDates(
  daysAhead: number = 14,
  startFrom?: Date,
): DateEntry[] {
  const dates: DateEntry[] = [];
  const start = startFrom ? new Date(startFrom) : new Date();
  for (let i = 0; i < daysAhead; i++) {
    const d = addDays(start, i);
    dates.push({
      dayKey: DAY_KEYS[d.getDay()],
      monthKey: MONTH_KEYS[d.getMonth()],
      date: d.getDate(),
      fullDate: toLocalDateStr(d),
    });
  }
  return dates;
}

function dateToEntry(date: Date): DateEntry {
  return {
    dayKey: DAY_KEYS[date.getDay()],
    monthKey: MONTH_KEYS[date.getMonth()],
    date: date.getDate(),
    fullDate: toLocalDateStr(date),
  };
}

export function dateStrToEntry(fullDate: string): DateEntry {
  // Use noon to avoid timezone shenanigans.
  return dateToEntry(new Date(fullDate + "T12:00:00"));
}

export function formatRSD(value: string | number, freeLabel?: string): string {
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (freeLabel && n === 0) return freeLabel;
  return new Intl.NumberFormat("sr-Latn-RS").format(n) + " RSD";
}

export function durationLabel(minutes: number): string {
  if (minutes >= 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  }
  return `${minutes}min`;
}

// Court names are usually numbers ("1", "2") but can also be people/place names
// ("Novak Đoković"). The "Court " prefix is only useful for numeric names —
// dropping it for text names also avoids overflow on toggle buttons.
export function formatCourtLabel(name: string, courtPrefix: string): string {
  return /^\d+$/.test(name.trim()) ? `${courtPrefix} ${name}` : name;
}

// Maps i18next language codes to BCP 47 tags used by the browser's locale-aware
// formatters (Intl.DateTimeFormat) and the native <input type="date"> picker.
// Serbian is rendered in Latin script throughout the app.
export function bcp47Locale(lang: string): string {
  if (lang === "sr") return "sr-Latn-RS";
  if (lang === "ru") return "ru-RU";
  return "en-US";
}
